"use client";

import { Check } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [showCheckmark, setShowCheckmark] = useState(false);
  const onSubmitWrapper = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit();
    if (!submitError) {
      setShowCheckmark(true);
      setTimeout(() => setShowCheckmark(false), 2000);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="p-8 glass-effect max-w-md mx-auto text-center">
        <div className="mb-4 flex justify-center">
          <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center animate-fade-in-up">
            <Check className="size-8 text-primary" strokeWidth={3} />
          </div>
        </div>
        <p className="text-foreground text-lg mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="bg-background/10 text-foreground border-border hover:bg-background/20">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={onSubmitWrapper} className="space-y-6 max-w-md mx-auto">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      },
      maxLength: {
        value: 50,
        message: "Name must be less than 50 characters"
      }
    }} render={({
      field
    }) => <FormItem className="animate-fade-in-up" data-index={0}>
            <FormLabel className="text-foreground">Name</FormLabel>
            <FormControl>
              <Input placeholder="Your Name" {...field} className="bg-background/10 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/40" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem className="animate-fade-in-up-delay-100" data-index={1}>
            <FormLabel className="text-foreground">Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" {...field} className="bg-background/10 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/40" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="animate-fade-in-up-delay-200" data-index={2}>
            <FormLabel className="text-foreground">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="How can we help you?" {...field} className="bg-background/10 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/40 min-h-32" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-md">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full btn-primary relative overflow-hidden">
        {showCheckmark ? <Check className="size-5 animate-fade-in-up" strokeWidth={3} /> : isSubmitting ? <>
            <span className="animate-spin mr-2">⏳</span>
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function GetInTouch() {
  return <section className="relative min-h-screen flex items-center justify-center py-16 px-4 bg-page-background" data-wvc-section="get-in-touch">
      <div className="absolute inset-0 bg-[#d7df23] backdrop-blur-sm" />
      
      <div className="relative z-10 w-full max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground-page mb-4">
            Get In Touch
          </h2>
          <p className="text-lg text-foreground-page max-w-2xl mx-auto">
            Have a question or want to learn more about our management recruitment solutions? We're here to help.
          </p>
        </div>

        <FormProvider className="glass-effect p-8 md:p-12" formId="contact-form-get-in-touch" sectionName="get-in-touch" defaultValues={{
        name: "",
        email: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 50
        },
        email: {
          required: true,
          pattern: "email"
        },
        message: {
          required: true,
          minLength: 10
        }
      }} submitText="Send Message" successMessage="Thank you for reaching out! We'll get back to you within 24 hours." errorMessage="Failed to send your message. Please try again or contact us directly at contact@activate.com">
          <ContactForm />
        </FormProvider>
      </div>
    </section>;
}